import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Invoice from '@spike/invoice-model';
import { Order } from '@spike/order-model';
import { status as AppointmentStatus } from '@spike/appointment-model';
import ResumeLine from 'components/Checkout2/UI/ResumeLine';
import TotalResumeLine from 'components/Checkout2/UI/TotalResumeLine';
import { formatAmount } from 'components/Checkout2/utils/OrderUtils';
import { FunctionComponent, useMemo } from 'react';
import moment from 'moment';
import { getTrainingOrWalkingDurationMinutes } from 'utils/appointmentServiceDuration';

export interface OrderSubtotalResumeProps {
    invoice?: Invoice;
    order?: Order;
    loadingInvoice: boolean;
    loadingOrder: boolean;
    cancelledStatus: Array<number>;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        resumeLine: {
            marginBottom: 12,
            [theme.breakpoints.down('sm')]: {
                marginBottom: 10
            }
        }
    })
);

export const OrderSubtotalResume: FunctionComponent<OrderSubtotalResumeProps> = props => {
    const classes = useStyles(props);

    const subtotal = useMemo(() => Number(props.order?.subtotal ?? 0).toFixed(2), [props.order]);

    const getAppointmentDurationMinutes = (item: NonNullable<Order['items']>['appointments'][number]) => {
        const businessAreaId = item.appointment.services[0]?.businessArea?.id;

        if (businessAreaId === 'walking' || businessAreaId === 'training') {
            return getTrainingOrWalkingDurationMinutes(item.appointment, businessAreaId);
        }

        return item.appointment.duration.to.clone().diff(item.appointment.duration.from.clone(), 'minutes');
    };

    const totalMinutes = useMemo(
        () =>
            props.order?.items.appointments
                .filter(item => !props.cancelledStatus.includes(item.appointment.status.id))
                .map(item => getAppointmentDurationMinutes(item))
                .reduce((total, minutes) => total + minutes, 0) ?? 0,
        [props.order]
    );

    const isBoardingService = useMemo(
        () =>
            props.order?.items.appointments.some(
                item => item.appointment.services[0]?.businessArea?.id === 'boarding'
            ) ?? false,
        [props.order]
    );

    const duration = isBoardingService
        ? (() => {
              const nights = Math.max(
                  ...(props.order?.items.appointments
                      .filter(item => !props.cancelledStatus.includes(item.appointment.status.id))
                      .map(item => item.appointment.nightsCount || 0) || [0])
              );
              return `${nights} Night${nights !== 1 ? 's' : ''}`;
          })()
        : totalMinutes < 60
        ? `${totalMinutes} min`
        : `${Math.trunc(totalMinutes / 60)} hr ${totalMinutes % 60} min`;

    return (
        <>
            <TotalResumeLine
                description="Subtotal"
                value={`$${subtotal} (${duration})`}
                loading={props.loadingOrder}
                className={classes.resumeLine}
            />
        </>
    );
};

export default OrderSubtotalResume;
